// The currently-selected demand, shared across every stage screen.
//
// Why this exists: the stage pages were reached two incompatible ways at once.
// App.js routes /triage and /discovery through react-router, while Dashboard.js
// renders all seven stages from its own `activeTab` string — and under Dashboard.js
// there is no Route, so the pages' useParams() returned {} and each one fell back to
// a hardcoded "DEM-001". Dashboard.js also passed demandId="DEM-001" explicitly to
// four of them. The result: picking a demand on one stage and navigating to the next
// silently reverted to DEM-001.
//
// This holds the selection in one place, seeds it from the URL when there is one,
// and keeps the URL in step so a stage screen stays linkable.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const DemandContext = createContext(null);
const STORAGE_KEY = "aiea.selectedDemandId";

/** Read a demand id out of /triage/DEM-XXXX style paths without depending on a Route. */
function demandIdFromPath() {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(
    /\/(?:triage|discovery|aoa|poc|requirements|security|tab|execution)\/([^/?#]+)/i
  );
  return match ? decodeURIComponent(match[1]) : null;
}

export function DemandProvider({ children }) {
  const [demandId, setDemandIdState] = useState(
    () => demandIdFromPath() || (typeof window !== "undefined" && window.sessionStorage
      ? window.sessionStorage.getItem(STORAGE_KEY)
      : null)
  );

  const setDemandId = useCallback((next) => {
    setDemandIdState(next || null);
    if (typeof window !== "undefined" && window.sessionStorage) {
      if (next) window.sessionStorage.setItem(STORAGE_KEY, next);
      else window.sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Keep the last path segment in step with the selection, so a stage screen stays
  // copy-pasteable. replaceState rather than push: switching demands is a filter
  // change, not a navigation, and shouldn't fill the back button.
  useEffect(() => {
    if (!demandId || typeof window === "undefined") return;
    const { pathname } = window.location;
    const stageMatch = pathname.match(
      /^(.*\/(?:triage|discovery|aoa|poc|requirements|security|tab|execution))(?:\/[^/?#]+)?$/i
    );
    if (stageMatch) {
      const next = `${stageMatch[1]}/${encodeURIComponent(demandId)}`;
      if (next !== pathname) window.history.replaceState({}, "", next);
    }
  }, [demandId]);

  const value = useMemo(() => ({ demandId, setDemandId }), [demandId, setDemandId]);
  return <DemandContext.Provider value={value}>{children}</DemandContext.Provider>;
}

/**
 * The selected demand id and a setter.
 *
 * Returns {demandId: null} when nothing is selected — callers should render an
 * empty state rather than substituting a default id. Defaulting to "DEM-001" is
 * what made the old screens show another demand's data without saying so.
 */
export function useDemandId() {
  const ctx = useContext(DemandContext);
  if (!ctx) {
    throw new Error("useDemandId must be used inside a <DemandProvider>");
  }
  return ctx;
}

export default DemandContext;
